import { Badge, Button, Flex, Tabs } from "antd";
import type { TabsProps } from "antd";
import type { IntegrationSyncStatus, ProductItemIntegrationSyncPageResponse } from "apiClients/base-generated";

/** Matches API / backend enum numeric values. */
export const IntegrationSyncStatusCode = {
  NotSynced: 0,
  Synced: 1,
  Paused: 2,
  Failed: 3,
} as const satisfies Record<string, IntegrationSyncStatus>;

/** Visual tab order (values still match API enum). Synced first as the default “happy path”. */
export const INTEGRATION_SYNC_TAB_ORDER: readonly IntegrationSyncStatus[] = [1, 0, 2, 3];

/** JSON keys on `countBySyncStatus` from the API (PascalCase enum names). */
const COUNT_BY_SYNC_STATUS_JSON_KEY: Record<IntegrationSyncStatus, keyof typeof IntegrationSyncStatusCode> = {
  0: "NotSynced",
  1: "Synced",
  2: "Paused",
  3: "Failed",
};

const TAB_META: Record<
  IntegrationSyncStatus,
  { label: string; badgeColor: string }
> = {
  0: { label: "Aldrig synkade artiklar", badgeColor: "#bfbfbf" },
  1: { label: "Synkade artiklar", badgeColor: "#52c41a" },
  2: { label: "Pausade artiklar", badgeColor: "#fa8c16" },
  3: { label: "Misslyckade artiklar", badgeColor: "#ff4d4f" },
};

export function integrationSyncStatusLabel(status: IntegrationSyncStatus): string {
  return TAB_META[status].label;
}

function countForSyncStatus(
  status: IntegrationSyncStatus,
  countBySyncStatus: ProductItemIntegrationSyncPageResponse["countBySyncStatus"],
  counts: Partial<Record<IntegrationSyncStatus, number>> | undefined,
): number {
  const override = counts?.[status];
  if (override !== undefined) {
    return override;
  }
  if (!countBySyncStatus) {
    return 0;
  }
  const pascalKey = COUNT_BY_SYNC_STATUS_JSON_KEY[status];
  const raw =
    countBySyncStatus[pascalKey] ?? countBySyncStatus[String(status)];
  return typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
}

function tabLabel(text: string, count: number, color: string) {
  return (
    <Flex align="center" gap={8}>
      <span>{text}</span>
      <Badge
        count={count}
        overflowCount={Number.MAX_SAFE_INTEGER}
        showZero
        color={color}
        style={{ minWidth: 22, height: 22, lineHeight: "22px", padding: "0 6px", fontSize: 12 }}
      />
    </Flex>
  );
}

export type IntegrationTabsProps = {
  /** Active tab: `IntegrationSyncStatus` (0–4). */
  activeStatus: IntegrationSyncStatus;
  onChange: (status: IntegrationSyncStatus) => void;
  /** Totals from API (`countBySyncStatus`); keys are PascalCase enum names (`NotSynced`, `Synced`, …) or `"0"`…`"4"`. */
  countBySyncStatus?: ProductItemIntegrationSyncPageResponse["countBySyncStatus"];
  /** Optional per-status overrides on top of `countBySyncStatus` (e.g. mocks). */
  counts?: Partial<Record<IntegrationSyncStatus, number>>;
  /** Visar ”Lägg till artiklar för synk” till höger när satt (t.ex. massimport-modal). */
  onAddArticlesForSync?: () => void;
  className?: string;
  size?: TabsProps["size"];
};

export const IntegrationTabs: React.FC<IntegrationTabsProps> = ({
  activeStatus,
  onChange,
  countBySyncStatus,
  counts,
  onAddArticlesForSync,
  className,
  size = "middle",
}) => {
  const items: TabsProps["items"] = INTEGRATION_SYNC_TAB_ORDER.map((status) => {
    const meta = TAB_META[status];
    const n = countForSyncStatus(status, countBySyncStatus, counts);
    return {
      key: String(status),
      label: tabLabel(meta.label, n, meta.badgeColor),
    };
  });

  return (
    <Flex justify="space-between" align="center" gap={16} style={{ width: "100%" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Tabs
          activeKey={String(activeStatus)}
          onChange={(key) => onChange(Number(key) as IntegrationSyncStatus)}
          items={items}
          size={size}
          className={className}
        />
      </div>
      {onAddArticlesForSync ? (
        <Button type="primary" onClick={onAddArticlesForSync}>
          Lägg till artiklar
        </Button>
      ) : null}
    </Flex>
  );
};
